import { Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider.js';
import { useMenu } from '../../../shared/hooks/useMenu.js';
import { ForbiddenPage } from '../screens/error/ForbiddenPage.js';

/**
 * RequireSectionAccess — permission-aware route guard for the GR §L security
 * screens (L30.*), used in composition with {@link RequireAuth}:
 *
 *   <RequireAuth>
 *     <RequireSectionAccess section="/security">
 *       <SomeSecurityScreen />
 *     </RequireSectionAccess>
 *   </RequireAuth>
 *
 * Access is NOT decided by any hardcoded role name or section literal in code.
 * It is decided entirely by the administrator through the Role Manager
 * permission matrix, whose result is surfaced to the client via
 * `GET /api/me/menu` (see {@link useMenu}). A signed-in user whose administrator
 * has not granted them any page under `section` (e.g. "/security") is shown the
 * 403 {@link ForbiddenPage}.
 *
 * This honours the permanent "ADMIN DECIDES PAGE ACCESS" rule (nothing is
 * hardcoded as admin-only) while satisfying GR C7 / Q10 — a user without the
 * SECURITY_OFFICER grant visiting `/security/*` gets 403 — because
 * SECURITY_OFFICER is simply whichever role the admin has granted the security
 * pages to in the matrix, not a constant baked into this component.
 */
export function RequireSectionAccess({
  section,
  children,
}: {
  readonly section: string;
  readonly children: ReactNode;
}): ReactNode {
  const { user, token } = useAuth();
  const { menu, loading } = useMenu('/api', token, 'web');

  if (!user) return <Navigate to="/auth/login" replace />;
  if (loading) return <p role="status">Loading…</p>;

  const items = menu?.items ?? [];
  const granted = items.some(
    (it) => it.isVisible && (it.path === section || it.path.startsWith(`${section}/`)),
  );

  if (!granted) return <ForbiddenPage />;
  return <>{children}</>;
}
